import { escapeHtml } from '../util/escapeHtml.js';
/**
 * Level-up celebration modal.
 *
 * A global watcher subscribes to the store ONCE (mountLevelUpWatcher, called
 * from main.ts) and remembers the character's last-seen level. Whenever the
 * level ticks upward — from training XP, deeds, expeditions, anything that
 * grows total XP past the next band — we pop a themed congratulations splash.
 *
 * BALANCE-SAFE: this modal is purely celebratory. It NEVER grants stats,
 * crowns, or items. Levels in Rex Mortis are XP-derived and confer no
 * mechanical bonus on their own (stats come from training); the modal just
 * surfaces the milestone, shows progress into the new level, and announces a
 * Level-Rank promotion when one happens.
 */
import type { CharacterDto } from '@rex-mortis/shared';
import { store } from '../state/store.js';
import { getTitleDef } from './Title.js';
import { formatBigStr } from '../util/formatBig.js';

/** Tracks the last level + rank we've already celebrated, per character id, so
 *  we don't re-fire on every store push at the same level — and so switching
 *  characters (different id) doesn't spuriously trigger a level-up. We keep the
 *  rank slug too so a Level-Rank promotion can be detected by simple slug
 *  comparison (no client-side level→rank table needed). */
interface Seen {
  id: string;
  level: number;
  rankSlug: string;
  /** Resource maxes captured at last sight, so a level-up can show the true
   *  gain (current − previous) without hard-coding the per-level deltas. */
  vigorMax: number;
  resolveMax: number;
  lifeMax: number;
  spiritMax: number;
}
let lastSeen: Seen | null = null;

const snap = (c: CharacterDto): Seen => ({
  id: c.id,
  level: c.level,
  rankSlug: c.levelRankSlug,
  vigorMax: c.vigorMax,
  resolveMax: c.resolveMax,
  lifeMax: c.lifeMax,
  spiritMax: c.spiritMax,
});

/** The resource-max increases earned across a level-up (each clamped to ≥0). */
export interface MaxGains {
  vigor: number;
  resolve: number;
  life: number;
  spirit: number;
}

/** Guard so we only ever attach one subscription. */
let mounted = false;

export function mountLevelUpWatcher(): void {
  if (mounted) return;
  mounted = true;

  // Seed from current state without celebrating — the player didn't just
  // level up by loading the page.
  const c0 = store.get().character;
  if (c0) lastSeen = snap(c0);

  store.subscribe((s) => {
    const c = s.character;
    if (!c) {
      lastSeen = null;
      return;
    }
    // First time we see this character (e.g. after login / character switch):
    // record without celebrating.
    if (!lastSeen || lastSeen.id !== c.id) {
      lastSeen = snap(c);
      return;
    }
    if (c.level > lastSeen.level) {
      const from = lastSeen.level;
      const fromRank = lastSeen.rankSlug;
      const gains: MaxGains = {
        vigor: Math.max(0, c.vigorMax - lastSeen.vigorMax),
        resolve: Math.max(0, c.resolveMax - lastSeen.resolveMax),
        life: Math.max(0, c.lifeMax - lastSeen.lifeMax),
        spirit: Math.max(0, c.spiritMax - lastSeen.spiritMax),
      };
      lastSeen = snap(c);
      showLevelUpModal(c, from, fromRank, gains);
    } else {
      // Level unchanged or (shouldn't happen) decreased — keep the baseline
      // current so future deltas stay honest.
      lastSeen = snap(c);
    }
  });
}

/** Shows the celebration. Exposed for manual/testing use too. */
export function showLevelUpModal(
  c: CharacterDto,
  fromLevel: number,
  fromRankSlug?: string,
  gains?: MaxGains,
): void {
  ensureStyles();

  // Level-Rank promotion = the auto-derived rank slug changed across this jump.
  // The watcher hands us the slug the player held before the level-up, so a
  // straight inequality is all we need — no client-side level→rank table.
  const rankDef = getTitleDef(c.levelRankSlug);
  const rankText = rankDef?.text ?? null;
  const isPromotion =
    rankText != null && fromRankSlug != null && fromRankSlug !== c.levelRankSlug;

  // XP progress into the freshly-reached level.
  let into = 0n;
  let toNext = 0n;
  try {
    into = BigInt(c.xpIntoLevel);
    toNext = BigInt(c.xpToNextLevel);
  } catch {
    /* leave at zero */
  }
  const span = into + toNext;
  const pct = span > 0n ? Number((into * 10_000n) / span) / 100 : 100;
  const progressLabel =
    toNext > 0n
      ? `${formatBigStr(c.xpIntoLevel)} / ${formatBigStr(span.toString())} XP toward Level ${c.level + 1}`
      : 'Highest XP band reached';

  const overlay = document.createElement('div');
  overlay.className = 'levelup-overlay';

  const modal = document.createElement('div');
  modal.className = 'levelup-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-label', `Level ${c.level} reached`);

  const jumped = c.level - fromLevel;
  const subhead =
    jumped > 1
      ? `You climbed ${jumped} levels — now Level ${c.level}!`
      : `You have reached Level ${c.level}!`;

  const promoHtml =
    isPromotion && rankText
      ? `<div class="levelup-rank"><span class="levelup-rank-kicker">New Rank</span><span class="levelup-rank-name">${escapeHtml(rankText)}</span></div>`
      : rankText
        ? `<div class="levelup-rank-quiet">Rank: ${escapeHtml(rankText)}</div>`
        : '';

  // Resource-max gains earned across this level-up. Built from the true deltas
  // (current − previous maxes) the watcher captured, so it reflects exactly
  // what changed — including any membership multiplier — and never drifts from
  // the balance constants. Only non-zero bars are shown (Spirit scales via
  // Domiciles, not levels, so it's normally absent).
  const gainPills: string[] = [];
  if (gains) {
    if (gains.vigor > 0) gainPills.push(`+${gains.vigor} Vigor`);
    if (gains.resolve > 0) gainPills.push(`+${gains.resolve} Resolve`);
    if (gains.life > 0) gainPills.push(`+${gains.life} Life`);
    if (gains.spirit > 0) gainPills.push(`+${gains.spirit} Spirit`);
  }
  const gainsHtml =
    gainPills.length > 0
      ? `<div class="levelup-gains">
           <span class="levelup-gains-kicker">Max increased</span>
           <div class="levelup-gains-pills">${gainPills
             .map((g) => `<span class="levelup-gain-pill">${g}</span>`)
             .join('')}</div>
         </div>`
      : '';

  modal.innerHTML = `
    <div class="levelup-crest">✦</div>
    <h2 class="levelup-title">Level Up</h2>
    <p class="levelup-sub">${escapeHtml(subhead)}</p>
    ${promoHtml}
    ${gainsHtml}
    <div class="levelup-progress">
      <div class="levelup-progress-track"><div class="levelup-progress-fill" style="width:0%"></div></div>
      <div class="levelup-progress-label">${escapeHtml(progressLabel)}</div>
    </div>
    <p class="levelup-flavour">Your deeds echo through the realm. Keep training to grow your strength.</p>
    <div class="levelup-actions">
      <button class="btn gold" id="levelup-close">Onward</button>
    </div>
  `;

  document.body.append(overlay);
  document.body.append(modal);

  // Animate the fill after mount so the bar visibly sweeps to its value.
  const fill = modal.querySelector<HTMLDivElement>('.levelup-progress-fill');
  requestAnimationFrame(() => {
    if (fill) fill.style.width = `${pct.toFixed(1)}%`;
  });

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    overlay.classList.add('levelup-leaving');
    modal.classList.add('levelup-leaving');
    setTimeout(() => {
      overlay.remove();
      modal.remove();
    }, 180);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' || e.key === 'Enter') close();
  };

  overlay.addEventListener('click', close);
  modal.querySelector<HTMLButtonElement>('#levelup-close')?.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  setTimeout(() => modal.querySelector<HTMLButtonElement>('#levelup-close')?.focus(), 0);
}

function ensureStyles(): void {
  if (document.getElementById('levelup-styles')) return;
  const style = document.createElement('style');
  style.id = 'levelup-styles';
  style.textContent = LEVELUP_STYLES;
  document.head.append(style);
}

const LEVELUP_STYLES = `
  .levelup-overlay {
    position: fixed;
    inset: 0;
    background: rgba(20, 12, 5, 0.62);
    z-index: 400;
    backdrop-filter: blur(2px);
    animation: levelup-fade 0.22s ease both;
  }
  .levelup-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, 92vw);
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius-lg);
    box-shadow:
      0 0 0 1px var(--c-gold),
      0 0 36px rgba(198, 160, 74, 0.35),
      0 18px 48px rgba(0, 0, 0, 0.55);
    padding: var(--s-5);
    z-index: 401;
    text-align: center;
    animation: levelup-pop 0.34s cubic-bezier(0.22, 1.2, 0.4, 1) both;
  }
  .levelup-overlay.levelup-leaving { animation: levelup-fade 0.18s ease reverse both; }
  .levelup-modal.levelup-leaving { animation: levelup-fade 0.18s ease reverse both; }
  .levelup-crest {
    font-size: 2.6rem;
    line-height: 1;
    color: var(--c-gold);
    text-shadow: 0 0 14px rgba(198, 160, 74, 0.55);
    margin-bottom: var(--s-2);
    animation: levelup-crest 0.6s ease both;
  }
  .levelup-title {
    font-family: var(--font-heading);
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-size: 1.5rem;
    color: var(--c-oxblood);
    margin: 0 0 var(--s-2) 0;
  }
  .levelup-sub {
    font-family: var(--font-body);
    font-size: 1.08rem;
    color: var(--c-ink);
    margin: 0 0 var(--s-3) 0;
  }
  .levelup-rank {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--s-2) var(--s-4);
    margin: 0 0 var(--s-3) 0;
    border: 1px solid var(--c-gold);
    border-radius: var(--radius-md);
    background: rgba(198, 160, 74, 0.12);
  }
  .levelup-rank-kicker {
    font-family: var(--font-heading);
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--c-gold-deep, #8a6d2a);
  }
  .levelup-rank-name {
    font-family: var(--font-heading);
    font-size: 1.12rem;
    font-weight: 700;
    color: var(--c-oxblood);
  }
  .levelup-rank-quiet {
    font-family: var(--font-body);
    font-size: 0.92rem;
    color: var(--c-ink-soft, #6b5a44);
    margin: 0 0 var(--s-3) 0;
  }
  .levelup-gains {
    margin: 0 0 var(--s-4) 0;
  }
  .levelup-gains-kicker {
    display: block;
    font-family: var(--font-heading);
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--c-gold-deep, #8a6d2a);
    margin-bottom: var(--s-2);
  }
  .levelup-gains-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: center;
  }
  .levelup-gain-pill {
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--c-ink);
    padding: 3px 10px;
    border: 1px solid var(--c-gold);
    border-radius: 999px;
    background: rgba(198, 160, 74, 0.12);
  }
  .levelup-progress { margin: 0 0 var(--s-4) 0; }
  .levelup-progress-track {
    height: 12px;
    border: 1px solid var(--c-gold);
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }
  .levelup-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #b8902f, #e7c869);
    border-radius: 999px;
    transition: width 0.9s cubic-bezier(0.25, 1, 0.4, 1);
  }
  .levelup-progress-label {
    font-family: var(--font-body);
    font-size: 0.82rem;
    color: var(--c-ink-soft, #6b5a44);
    margin-top: var(--s-2);
  }
  .levelup-flavour {
    font-family: var(--font-body);
    font-style: italic;
    font-size: 0.92rem;
    color: var(--c-ink-soft, #6b5a44);
    margin: 0 0 var(--s-4) 0;
    line-height: 1.45;
  }
  .levelup-actions { display: flex; justify-content: center; }
  @keyframes levelup-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes levelup-pop {
    from { opacity: 0; transform: translate(-50%, -46%) scale(0.92); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  @keyframes levelup-crest {
    0% { transform: scale(0.4) rotate(-12deg); opacity: 0; }
    60% { transform: scale(1.18) rotate(4deg); opacity: 1; }
    100% { transform: scale(1) rotate(0); opacity: 1; }
  }
  @media (max-width: 480px) {
    .levelup-modal { padding: var(--s-4); }
    .levelup-title { font-size: 1.3rem; }
  }
`;
